<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading || !isReadyToScan">
    <div class="step-label">
      {{ $t('bulk_tracking') }}
    </div>
    <div class="barcode-scanner relative">
      <div id="scan-area" class="relative">
        <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
          <IconSwitch />
        </div>
        <div id="video-area">
          <video playsinline autoplay id="video"></video>
        </div>
      </div>

      <BarcodeSymbologiesSelection
        class="absolute bottom-2 z-50 left-2 flex w-52"
        @update:onSymbologiesChange="setBarcodeSymbologies"
        v-model:symbologies="symbologies"
      />

      <ScanArea />
    </div>

    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
        {{ bulkUpdateOperationLimitCount ? `/${bulkUpdateOperationLimitCount?.maxValue}` : '' }}
      </span>
    </div>
    <el-button
      circle
      type="primary"
      class="submit-button"
      :disabled="
        scannedBarcodeList.length < (bulkUpdateOperationLimitCount ? bulkUpdateOperationLimitCount?.minValue : 1)
      "
      @click="submit()"
    >
      {{ $t('OK') }}
    </el-button>
    <SocketMobileScanComponent v-model:loading="loading" :on-scan="onSocketMobileScan" />
  </div>
  <el-dialog
    center
    :lock-scroll="true"
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :close-on-press-escape="!pendingSubmit"
    :close-on-click-modal="false"
    :title="$t('Scanned Barcodes')"
  >
    <template v-if="dialogVisible">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <div v-else>
        <BarcodeLabel
          v-for="bc in displayScannedBarcodes"
          :key="bc.id"
          :type="bc.barcodeType || 'external'"
          :label="bc.label || bc?.id || ''"
          :subLabel="bc.id"
          :labelOnly="!bc.label || !bc?.id"
          :externalId="bc.externalId"
          :class="{ 'has-notification': barcodesWithNotifications[bc.id] }"
        >
          <template v-slot:action-menu v-if="barcodesWithNotifications[bc.id] || barcodesLoadingNotifications[bc.id]">
            <span v-if="barcodesLoadingNotifications[bc.id]" class="notification-loading" />
            <IconInfo v-else class="cursor-pointer notification-icon" @click="onNotificationIconClick(bc)" />
          </template>
        </BarcodeLabel>
      </div>
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('Done') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
  <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />

  <BarcodeNotificationDialog
    :model-value="currentNotificationBarcode !== null"
    :message="currentNotificationMessage"
    @update:model-value="currentNotificationBarcode = null"
    @confirm="onNotificationConfirm"
    @remove="onNotificationRemove"
  />
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import ScanArea from '@/components/svg/ScanArea.vue'
import BarcodeLabel from '@/components/BarcodeLabel.vue'
import { ADD_TRACKING, FETCH_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IProject, ITrackingRequestBody } from 'smartbarcode-web-core/src/utils/types/index'
import MultipleBarcodeScanMixin from '@/components/mixins/MultipleBarcodeScanMixin.vue'
import { tracking } from '@/utils/api'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import { cloneDeep } from 'lodash'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeNotificationDialog from '@/components/dialog/BarcodeNotificationDialog.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import IconInfo from '@/components/svg/IconInfo.vue'

@Options({
  components: {
    ScanArea,
    BarcodeLabel,
    BarcodeSymbologiesSelection,
    IconSwitch,
    IconInfo,
    SelectionCameraPopup,
    BarcodeNotificationDialog,
  },
  name: 'BulkTracking',
})
export default class MultipleBarcodeScan extends mixins(MultipleBarcodeScanMixin) {
  loading = false
  requestBody = {} as ITrackingRequestBody
  nextSelectTrackingPointName = ''
  isReadyToScan = true

  get barcode(): IBarcode {
    return this.$store.state.barcode.barcode
  }

  removeScannedBarcode(bcId: string) {
    this.scannedBarcodeList = this.scannedBarcodeList.filter((b: IBarcode) => b.id !== bcId)
    this.barcodes = this.barcodes.filter((b: IBarcode) => b.id !== bcId)
    const ids = this.requestBody?.barcodeIds as string[]
    if (ids) {
      const idx = ids.indexOf(bcId)
      if (idx !== -1) ids.splice(idx, 1)
    }
    const updated = { ...this.barcodesWithNotifications }
    delete updated[bcId]
    this.barcodesWithNotifications = updated
  }

  async created() {
    this.isForcedHideMultipleScan = true
    if (!this.$store.state.barcode.barcode) {
      await this.$store.dispatch(FETCH_BARCODE, {
        id: this.$route.params.barcodeId,
        trackingNumber: this.trackingNumberFromPath,
        externalId: this.externalIdFromPath,
        path: this.queryPath,
      })
    }
    this.nextSelectTrackingPointName = this.$route.params.nextTrackName
    this.requestBody = JSON.parse(localStorage.tempRequestForMultipleBarcodeScan)
    this.scannedBarcodeList.push(cloneDeep(this.barcode))
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    await this.onLeaveGuard(to, from, next)
  }

  get bulkUpdateOperationLimitCount() {
    const project = this.$store.state.project.details as IProject
    const projectBarcode = project.barcodeTypes?.[this.$store.getters.barcodeType]
    return projectBarcode?.bulkUpdateOperationLimitCount
  }

  addBarcode(barcode: string) {
    ;(this.requestBody?.barcodeIds as string[]).push(barcode)
  }

  async validateBarcode(resultScanned: IBarcode) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType } = resultScanned
    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }
    const data = {
      barcodeId: this.barcode?.id,
      barcodeIds: [resultScanned?.id],
      trackingData: this.requestBody.trackingData,
      selectedNextTrackpoint: this.nextSelectTrackingPointName,
      isDryRun: true,
    }

    await tracking(data)
  }

  forward() {
    this.$store.dispatch(SAVE_SCANNED_BARCODES, [...this.barcodes])
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('bulk_tracking_read_result'),
        operation: ADD_TRACKING,
        extParams: JSON.stringify({
          trackingData: this.requestBody.trackingData,
          selectedNextTrackpoint: this.nextSelectTrackingPointName,
        }),
      },
    })
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.step-label {
  margin-bottom: 25px;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
}

#scan-area,
#video-area {
  width: 100%;
  height: 100%;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}

.barcode-number-block {
  margin-top: 20px;
  margin-bottom: 20px;
}

.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
}

:deep(.action-menu) {
  width: auto;
  margin-left: 8px;
}

:deep(.has-notification.barcode-label-container) {
  border: 2px solid #e6a23c;
}
</style>
